/**
 * The region palette, shared down the Venues page (BON-538).
 *
 * A context rather than a prop because the map is needed in three places that
 * are five hops apart — the roster's column model, the phone card, and the
 * map's GeoJSON — and threading one value through `VenuesBody`, the roster card
 * and the card stack adds a parameter to every component in between that none
 * of them has any use for.
 *
 * Empty by default, so a component rendered outside the page (a test, a future
 * surface) falls back to the unassigned colour rather than throwing.
 */
import { createContext, useCallback, useContext } from 'react';

import type { RegionColors } from './region-colors';
import { UNASSIGNED_COLOR } from './region-colors';
import type { VenueRosterRow } from './venues-roster-api';

export const RegionColorsContext = createContext<RegionColors>(new Map());

/**
 * `(region) => colour`, for a roster row's region or any other.
 *
 * Memoised on the map, and that matters: the map component takes this as a
 * prop, and a fresh closure every render re-fired the effect that pushes new
 * pin data — which is what made the map jump back to its full extent the
 * instant anything hovered.
 */
export function useRegionColor(): (region: VenueRosterRow['region']) => string {
  const colors = useContext(RegionColorsContext);
  return useCallback(
    (region: VenueRosterRow['region']) =>
      region ? (colors.get(region.id) ?? UNASSIGNED_COLOR) : UNASSIGNED_COLOR,
    [colors],
  );
}
